import * as React from 'react';
import classnames from 'classnames';
import * as Currency from 'core/utils/Currency';
import { Box, Button, HStack, VStack } from '@chakra-ui/react';
import * as Images from 'pos/Images';
import { SvgIcon } from 'core/ui-next';

type ButtonProps = React.PropsWithChildren<{
	button: string;
	functionButton?: boolean;
	handleButton: (button: string) => void;
}>;

export const KeypadButton: React.FC<ButtonProps> = ({ button, handleButton, functionButton = false }: ButtonProps): React.ReactNode => {
	const classes = classnames({ 'keypad-button': true, 'btn btn-default': true, 'keypad-function-btn': functionButton });
	return <div onClick={() => handleButton(button)} className={classes}><span>{getButtonDisplayName(button)}</span></div>;
};

function getDefaultDiplay(): { [key: string]: string } {
	return {
		'{clr}': 'Clr',
		'{bksp}': 'Backspace',
		'{backspace}': 'Nackspace',
		'{enter}': 'Enter',
		'{shift}': 'Shift',
		'{shiftleft}': 'Shift',
		'{shiftright}': 'Shift',
		'{alt}': 'Alt',
		'{s}': 'Shift',
		'{tab}': 'Tab',
		'{lock}': 'Caps',
		'{capslock}': 'Caps',
		'{accept}': 'Submit',
		'{space}': ' ',
		'{//}': ' ',
		'{esc}': 'Esc',
		'{escape}': 'Esc',
		'{f1}': 'F1',
		'{f2}': 'F2',
		'{f3}': 'F3',
		'{f4}': 'F4',
		'{f5}': 'F5',
		'{f6}': 'F6',
		'{f7}': 'F7',
		'{f8}': 'F8',
		'{f9}': 'F9',
		'{f10}': 'F10',
		'{f11}': 'F11',
		'{f12}': 'F12',
		'{numpaddivide}': '/',
		'{numlock}': 'Lock',
		'{arrowup}': '↑',
		'{arrowleft}': '←',
		'{arrowdown}': '↓',
		'{arrowright}': '→',
		'{prtscr}': 'print',
		'{scrolllock}': 'Scroll',
		'{pause}': 'Pause',
		'{insert}': 'Ins',
		'{home}': 'Home',
		'{pageup}': 'Up',
		'{delete}': 'Del',
		'{end}': 'End',
		'{pagedown}': 'Down',
		'{numpadmultiply}': '*',
		'{numpadsubtract}': '-',
		'{numpadadd}': '+',
		'{numpadenter}': 'Enter',
		'{period}': '.',
		'{numpaddecimal}': Currency.DECIMAL_SEPARATOR,
		'{numpad0}': '0',
		'{numpad1}': '1',
		'{numpad2}': '2',
		'{numpad3}': '3',
		'{numpad4}': '4',
		'{numpad5}': '5',
		'{numpad6}': '6',
		'{numpad7}': '7',
		'{numpad8}': '8',
		'{numpad9}': '9'
	};
}

function getButtonDisplayName(button: string): string {
	return getDefaultDiplay()[button] || button;
}

type EnterButtonProps = {
	handleButton: (button: string) => void;
};

export const EnterButton = (props: EnterButtonProps) => {
	const { handleButton } = props;
	return (
		<Button h='full' w='full' bg={'secondary'} _hover={{ bg: 'secondary._hover' }} _active={{ bg: 'secondary._active !important' }} onClick={() => handleButton('{enter}')} paddingY={6} paddingX={3} shadow={'sm'} borderRadius={'md'}>
			<VStack gap='2.5'>
				<Box fontWeight={'bold'} fontSize={'lg'} color={{ base: 'white', _dark: 'white' }}>{getButtonDisplayName('{enter}')}</Box>
				<SvgIcon h='6' w='6' color={{ base: 'white', _dark: 'white' }} src={Images.Enter} />
			</VStack>
		</Button>
	);
};

type NumericButtonProps = {
	button: string;
	handleButton: (button: string) => void;
};

export const NumericButton = (props: NumericButtonProps) => {
	const { button, handleButton } = props;
	return (
		<Button h='full' w='full'
			bg={{ base: 'white', _dark: '{colors.neutral.800}' }}
			_hover={{ bg: { base: '{colors.neutral.200}', _dark: '{colors.neutral.700}' } }}
			_active={{ bg: { base: '{colors.neutral.300} !important', _dark: '{colors.neutral.600} !important' } }}
			display={'flex'}
			alignItems={'center'}
			justifyContent={'center'}
			onClick={() => handleButton(button)}
			shadow={'sm'}
			borderRadius={'md'}
		>
			<Box fontWeight={'bold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }} fontSize={'2xl'}>{getButtonDisplayName(button)}</Box>
		</Button>
	);
};

type FunctionButtonProps = {
	text?: string;
	button?: string;
	direction?: 'row' | 'column';
	src?: SvgIcon;
	currencySymbol?: string;
	handleButton: (button: string) => void;
};

export const FunctionButton = (props: FunctionButtonProps) => {
	const { src, button, text, handleButton, direction = 'row', currencySymbol } = props;

	return (
		<Button h='full' w='full' bg={'secondary'} _hover={{ bg: 'secondary._hover' }} _active={{ bg: 'secondary._active !important' }} display={'flex'} alignItems={'center'} justifyContent={'center'} onClick={() => handleButton(button ?? '')} shadow={'sm'} borderRadius={'md'}>
			{
				direction === 'row' ?
					<HStack>
						{src ? <SvgIcon h='6' w='6' color={'white'} src={src} /> : null}
						{text ? <Box fontSize={'lg'} color={'white'}>{text}</Box> : null}
					</HStack>
					:
					<VStack gap={'0.5'}>
						{src ? <SvgIcon h='4' w='4' color={'white'} src={src} /> : null}
						{text ?
							<Box fontSize={'2xs'} fontWeight={'semibold'} color={'white'} textWrap={'pretty'} lineHeight={'120%'}>
								{currencySymbol ?
									<VStack gap={'0.5'} alignItems={'center'}>
										<Box fontSize={'md'} fontWeight={'normal'}>
											{currencySymbol}
										</Box>
										<Box fontSize={'2xs'} fontWeight={'semibold'}>
											{text}
										</Box>
									</VStack>
									: text
								}
							</Box>
							: null
						}
					</VStack>
			}
		</Button >
	);
};

type ShowKeypadButtonProps = {
	button: string;
	src: SvgIcon;
	isShowKeypad?: boolean;
	handleButton: (button: string) => void;
};

export const ShowKeypadButton = (props: ShowKeypadButtonProps) => {
	const { src, button, isShowKeypad, handleButton } = props;

	const isShowKeyPadStyles = isShowKeypad ? {
		'bg': { base: '{colors.neutral.500}', _dark: '{colors.teal.800}' },
		'border': '2px solid',
		'borderColor': { base: '{colors.neutral.800}', _dark: '{colors.neutral.300}' },
		'_hover': { bg: { base: '{colors.neutral.600}', _dark: '{colors.teal.600}' } },
		'_active': { bg: { base: '{colors.neutral.400} !important', _dark: '{colors.teal.900} !important' } },
		'color': { base: 'white', _dark: 'white' }
	} : {
		'bg': 'secondary',
		'border': 'none',
		'borderColor': 'none',
		'_hover': { bg: 'none' },
		'_active': { bg: 'none' },
		'color': 'white'
	};

	return (
		<Button
			h='full'
			w='full'
			bg={isShowKeyPadStyles.bg}
			border={isShowKeyPadStyles.border}
			borderColor={isShowKeyPadStyles.borderColor}
			_hover={isShowKeyPadStyles._hover}
			_active={isShowKeyPadStyles._active}
			display={'flex'}
			alignItems={'center'}
			justifyContent={'center'}
			paddingY={6}
			shadow={'sm'}
			borderRadius={'md'}
			onClick={() => handleButton(button ?? '')}
		>
			<HStack>
				<SvgIcon h='7' w='7' color={isShowKeyPadStyles.color} src={src} />
			</HStack>
		</Button >
	);
};